import type { AxiosError } from 'axios';
import { authClient } from './client';
import type {
  ConsignorDirectoryPayload,
  ConsignorDirectoryQuery,
  ConsignorDirectorySummary,
  ConsignorEditData,
  ConsignorRow,
  ConsignorUpdatePayload,
} from '../types/consignorDirectory';
import downloadBlob from '../utils/downloadBlob';

// ─── Parsers ──────────────────────────────────────────────────────────────────

function parseConsignorRow(raw: unknown): ConsignorRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = Number(r.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const commission = Number(r.commission ?? 0);
  const inStockPairs = Number(r.inStockPairs ?? r.in_stock_pairs ?? 0);
  const lifetimeSales = Number(r.lifetimeSales ?? r.lifetime_sales ?? 0);
  const currentBalance = Number(r.currentBalance ?? r.current_balance ?? 0);
  const phoneRaw = r.phone ?? r.phoneNumber;
  return {
    id,
    email: String(r.email ?? '').trim(),
    name: String(r.name ?? r.fullName ?? r.full_name ?? '').trim(),
    phone: typeof phoneRaw === 'string' && phoneRaw.trim() ? phoneRaw.trim() : null,
    isActive: Boolean(r.isActive ?? r.is_active ?? true),
    isIncomplete: Boolean(r.isIncomplete ?? r.is_incomplete ?? false),
    commission: Number.isFinite(commission) ? commission : 0,
    inStockPairs: Number.isFinite(inStockPairs) ? Math.round(inStockPairs) : 0,
    lifetimeSales: Number.isFinite(lifetimeSales) ? lifetimeSales : 0,
    currentBalance: Number.isFinite(currentBalance) ? currentBalance : 0,
  };
}

function parseSummary(raw: unknown): ConsignorDirectorySummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const totalConsignors = Number(r.totalConsignors ?? r.total_consignors ?? 0);
  const activeConsignors = Number(r.activeConsignors ?? r.active_consignors ?? 0);
  const activeInStockPairs = Number(r.activeInStockPairs ?? r.active_in_stock_pairs ?? 0);
  const pendingBalance = Number(r.pendingBalance ?? r.pending_balance ?? 0);
  if (!Number.isFinite(totalConsignors)) return null;
  return {
    totalConsignors: Math.round(totalConsignors),
    activeConsignors: Number.isFinite(activeConsignors) ? Math.round(activeConsignors) : 0,
    activeInStockPairs: Number.isFinite(activeInStockPairs) ? Math.round(activeInStockPairs) : 0,
    pendingBalance: Number.isFinite(pendingBalance) ? pendingBalance : 0,
  };
}

function parsePayload(
  body: unknown,
  fallbackPage: number,
  fallbackPageSize: number
): ConsignorDirectoryPayload {
  const empty: ConsignorDirectoryPayload = {
    items: [],
    total: 0,
    page: fallbackPage,
    pageSize: fallbackPageSize,
    summary: null,
  };
  if (!body || typeof body !== 'object') return empty;
  const o = body as Record<string, unknown>;
  const inner = (o.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
    string,
    unknown
  >;
  const rawItems = inner.items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(parseConsignorRow).filter((x): x is ConsignorRow => x !== null)
    : [];
  const total =
    typeof inner.total === 'number' && Number.isFinite(inner.total) ? inner.total : items.length;
  const page = typeof inner.page === 'number' && inner.page >= 1 ? inner.page : fallbackPage;
  const pageSize =
    typeof inner.pageSize === 'number' && inner.pageSize >= 1 ? inner.pageSize : fallbackPageSize;
  return { items, total, page, pageSize, summary: parseSummary(inner.summary) };
}

function buildParams(
  query: Omit<ConsignorDirectoryQuery, 'page' | 'pageSize'>
): Record<string, string> {
  const p: Record<string, string> = {};
  if (query.search?.trim()) p.search = query.search.trim();
  if (query.orderBy) p.orderBy = query.orderBy;
  if (query.orderDirection) p.orderDirection = query.orderDirection;
  return p;
}

// ─── Exported functions ───────────────────────────────────────────────────────

export type ConsignorDirectoryResult =
  | { ok: true; data: ConsignorDirectoryPayload }
  | { ok: false; error: string };

export async function fetchConsignorDirectory(
  query: ConsignorDirectoryQuery = {}
): Promise<ConsignorDirectoryResult> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? 25;
  try {
    const { data } = await authClient.get<unknown>('/api/admin/consignors/directory', {
      params: { ...buildParams(query), page, pageSize },
    });
    return { ok: true, data: parsePayload(data, page, pageSize) };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ??
      (ax.response?.status === 404
        ? 'El endpoint de consignadores aún no está disponible.'
        : null) ??
      'No se pudo cargar el directorio de consignadores.';
    return { ok: false, error: msg };
  }
}

// ─── Consignor edit ───────────────────────────────────────────────────────────

export type ConsignorEditResult =
  | { ok: true; data: ConsignorEditData }
  | { ok: false; error: string };

/**
 * GET /api/admin/consignors/:id
 *
 * Returns full profile for a single consignor including the INE photo URL.
 *
 * Response 200 JSON:
 * {
 *   "message": "OK",
 *   "data": {
 *     "id": 1,
 *     "email": "user@example.com",
 *     "name": "Full Name",
 *     "phone": "+52...",
 *     "commission": 30,
 *     "inePhotoUrl": "https://...",   // nullable
 *     "isActive": true,
 *     "isAdmin": false
 *   }
 * }
 *
 * Errors: 404 if consignor not found, 500 on server error.
 */
export async function fetchConsignorForEdit(id: number): Promise<ConsignorEditResult> {
  try {
    const { data } = await authClient.get<unknown>(`/api/admin/consignors/${id}`);
    const o = (data as Record<string, unknown>)?.data ?? data;
    const r = o as Record<string, unknown>;
    const parsed: ConsignorEditData = {
      id: Number(r.id),
      email: String(r.email ?? '').trim(),
      name: String(r.name ?? r.fullName ?? '').trim(),
      phone: typeof r.phone === 'string' && r.phone.trim() ? r.phone.trim() : null,
      commission: Number(r.commission ?? 0),
      inePhotoUrl: (() => {
        if (typeof r.inePhotoUrl === 'string' && r.inePhotoUrl.trim()) return r.inePhotoUrl.trim();
        if (typeof r.ine_photo_url === 'string' && r.ine_photo_url.trim())
          return r.ine_photo_url.trim();
        return null;
      })(),
      isActive: Boolean(r.isActive ?? r.is_active ?? true),
      isAdmin: Boolean(r.isAdmin ?? r.is_admin ?? false),
    };
    return { ok: true, data: parsed };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo cargar la información del consignador.',
    };
  }
}

export type ConsignorUpdateResult = { ok: true } | { ok: false; error: string };

/**
 * PATCH /api/admin/consignors/:id
 *
 * Partially updates a consignor's profile. All fields are optional.
 *
 * Request body (JSON):
 * {
 *   "name": "Full Name",       // optional string
 *   "email": "...",            // optional string
 *   "phone": "+52...",         // optional string | null
 *   "commission": 30,          // optional number 0–100
 *   "isActive": true,          // optional boolean
 *   "isAdmin": false           // optional boolean
 * }
 *
 * Response 200: { "message": "OK" }
 * Errors: 400 validation, 404 not found, 500 server error.
 */
export async function updateConsignor(
  id: number,
  payload: ConsignorUpdatePayload
): Promise<ConsignorUpdateResult> {
  try {
    await authClient.patch(`/api/admin/consignors/${id}`, payload);
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo actualizar el consignador.',
    };
  }
}

export type ResendOnboardingResult = { ok: true } | { ok: false; error: string };

/**
 * POST /api/admin/consignors/:id/resend-onboarding
 *
 * Mints a new 7-day finish-onboarding token and sends the WhatsApp template.
 * Only valid while the consignor is still incomplete.
 */
export async function resendConsignorOnboarding(id: number): Promise<ResendOnboardingResult> {
  try {
    await authClient.post(`/api/admin/consignors/${id}/resend-onboarding`, {});
    return { ok: true };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    return {
      ok: false,
      error: ax.response?.data?.message ?? 'No se pudo reenviar el WhatsApp de registro.',
    };
  }
}

export type ConsignorExportResult = { ok: true } | { ok: false; error: string };

export async function exportConsignorDirectory(
  query: Omit<ConsignorDirectoryQuery, 'page' | 'pageSize'>
): Promise<ConsignorExportResult> {
  try {
    const res = await authClient.get<Blob>('/api/admin/consignors/directory/export', {
      params: buildParams(query),
      responseType: 'blob',
    });
    downloadBlob(res.data, 'consignors-directory.csv');
    return { ok: true };
  } catch {
    return { ok: false, error: 'No se pudo exportar el directorio de consignadores.' };
  }
}
